const React = require("react");
const Component = React.Component;
const I18nextProvider = require("react-i18next").I18nextProvider;
const i18n = require("../../../i18n").default;

// Create React Contexts for modern context API
const RillContext = React.createContext({
  locals: null,
  req: null,
});

// StableContext holds a subset of locals that layout components need.
// Its value is memoized so it only changes when these specific properties
// change, preventing unnecessary re-renders of Header, Leftbar, etc.
const StableContext = React.createContext({ locals: {} });

const useRillContext = () => {
  const context = React.useContext(RillContext);
  return context ?? { locals: null, req: null };
};

const useStableContext = () => {
  return React.useContext(StableContext);
};

// Functional component that memoizes a stable subset of locals.
// Loader cache hits return the same object references, so useMemo
// skips re-creation when only page-specific data changes.
// `query` is excluded — it is a new object on every navigation, so layout
// components that need it receive it as a prop from their wrapper instead.
function StableProvider({ locals, children }) {
  const stableLocals = React.useMemo(
    () => ({
      activeUser: locals.activeUser,
      activeUserToRequest: locals.activeUserToRequest,
      currentActiveUser: locals.currentActiveUser,
      organizationOwner: locals.organizationOwner,
      organizationRoles: locals.organizationRoles,
      organizationPermissions: locals.organizationPermissions,
      organizationRoleName: locals.organizationRoleName,
      usersOrganization: locals.usersOrganization,
      pathname: locals.pathname,
      athlete: locals.athlete,
      userByEmail: locals.userByEmail,
      unreadNewsCount: locals.unreadNewsCount,
    }),
    [
      locals.activeUser,
      locals.activeUserToRequest,
      locals.currentActiveUser,
      locals.organizationOwner,
      locals.organizationRoles,
      locals.organizationPermissions,
      locals.organizationRoleName,
      locals.usersOrganization,
      locals.pathname,
      locals.athlete,
      locals.userByEmail,
      locals.unreadNewsCount,
    ]
  );

  const contextValue = React.useMemo(
    () => ({ locals: stableLocals }),
    [stableLocals]
  );

  return React.createElement(
    StableContext.Provider,
    { value: contextValue },
    children
  );
}

// Expose component and context.
module.exports = Base;
module.exports.RillContext = RillContext;
module.exports.useRillContext = useRillContext;
module.exports.StableContext = StableContext;
module.exports.useStableContext = useStableContext;

/**
 * React component that provides all sub components with a Rill context (includes locals and req).
 * @param {*} props - the properties for the component.
 * @param {*} ctx - the original context for the component.
 */
function Base(props, ctx) {
  Component.call(this, props, ctx);
}

// Extend react component.
Base.prototype = Object.create(Component.prototype);
Base.prototype.constructor = Base;

// Render with modern context provider.
Base.prototype.render = function () {
  const contextValue = {
    locals: this.props.locals,
    req: this.props.req,
  };

  return React.createElement(
    I18nextProvider,
    { i18n },
    React.createElement(
      RillContext.Provider,
      { value: contextValue },
      React.createElement(
        StableProvider,
        { locals: this.props.locals },
        this.props.view
      )
    )
  );
};
